/**
 * Screen Review data access — sessions, notes, triage.
 *
 * Redesigns vs the reference implementation it was ported from:
 *   - `createNote` mints ONE id used for both the storage file and the doc
 *     (the reference let addDoc assign a different id than the file name);
 *   - the active session uses a deterministic `session-YYYY-MM-DD` id, so two
 *     simultaneous first-saves merge instead of creating two sessions;
 *   - screenshot upload failure never loses the note (feedbackService
 *     convention: the doc is the system of record);
 *   - open-note counts use aggregation queries, not a 500-doc client scan.
 */
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../firebase/config';
import type {
  ReviewAnchor,
  ReviewCategory,
  ReviewNoteStatus,
  ReviewSeverity,
  ReviewShape,
  ScreenReviewNote,
  ScreenReviewSession,
} from '../../types/screenReview.types';

const SESSIONS = 'reviewSessions';
const NOTES = 'reviewNotes';

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

function toSession(id: string, d: Record<string, unknown>): ScreenReviewSession {
  return {
    id,
    title: String(d.title ?? ''),
    status: d.status === 'closed' ? 'closed' : 'open',
    createdAt: (d.createdAt as ScreenReviewSession['createdAt']) ?? null,
    closedAt: (d.closedAt as ScreenReviewSession['closedAt']) ?? null,
    aiSummary: (d.aiSummary as string) ?? null,
    aiSummaryGeneratedAt:
      (d.aiSummaryGeneratedAt as ScreenReviewSession['aiSummaryGeneratedAt']) ?? null,
  };
}

export async function listSessions(max = 50): Promise<ScreenReviewSession[]> {
  const snap = await getDocs(
    query(collection(db, SESSIONS), orderBy('createdAt', 'desc'), limit(max)),
  );
  return snap.docs.map((d) => toSession(d.id, d.data()));
}

/**
 * The session new notes attach to. One open session at a time by convention:
 * reuse the open one, else start one named for today. The deterministic
 * per-day doc id means two simultaneous first-saves write the SAME session
 * (merge), never two.
 */
export async function getOrCreateActiveSession(): Promise<ScreenReviewSession> {
  const open = await getDocs(
    query(collection(db, SESSIONS), where('status', '==', 'open'), limit(1)),
  );
  if (!open.empty) {
    const d = open.docs[0];
    return toSession(d.id, d.data());
  }
  const now = new Date();
  const id = `session-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const title = `Review — ${now.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;
  const sessionBody = {
    title,
    status: 'open',
    createdAt: serverTimestamp(),
    closedAt: null,
    aiSummary: null,
    aiSummaryGeneratedAt: null,
  };
  try {
    await setDoc(doc(db, SESSIONS, id), sessionBody, { merge: true });
    return { id, title, status: 'open', createdAt: null, closedAt: null, aiSummary: null, aiSummaryGeneratedAt: null };
  } catch {
    // The deterministic doc already exists (a concurrent first-save won the
    // race, or a SAME-DAY session was already closed) — for non-staff the
    // merge-set counts as an UPDATE and rules deny it. Never lose the note:
    // re-query for the winner's open session, else create a fresh
    // random-id session (create is allowed for any signed-in user).
    const retry = await getDocs(
      query(collection(db, SESSIONS), where('status', '==', 'open'), limit(1)),
    );
    if (!retry.empty) {
      const d = retry.docs[0];
      return toSession(d.id, d.data());
    }
    const fresh = doc(collection(db, SESSIONS));
    await setDoc(fresh, sessionBody);
    return { id: fresh.id, title, status: 'open', createdAt: null, closedAt: null, aiSummary: null, aiSummaryGeneratedAt: null };
  }
}

/** Close a session — from the Support Hub, ending the round of review. */
export async function closeSession(sessionId: string): Promise<void> {
  await updateDoc(doc(db, SESSIONS, sessionId), {
    status: 'closed',
    closedAt: serverTimestamp(),
  });
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

export interface CreateNoteInput {
  sessionId: string;
  userId: string;
  userAuthUid: string;
  userName: string;
  userEmail: string;
  userRole: string;
  body: string;
  category: ReviewCategory;
  severity: ReviewSeverity;
  shapes: ReviewShape[];
  anchor: ReviewAnchor;
  viewportW: number;
  viewportH: number;
  /** JPEG from `captureScreenshot()` (redactions burned in); null when capture failed. */
  screenshot: Blob | null;
  userAgent: string;
  appVersion: string;
  consoleErrors: string[];
}

export async function createNote(input: CreateNoteInput): Promise<string> {
  // One minted id names BOTH the storage file and the note doc.
  const noteRef = doc(collection(db, NOTES));
  const noteId = noteRef.id;

  // Screenshot first: the note doc stores the resolved download URL, so
  // consumers (Support Hub, notification emails) never resolve paths.
  let screenshotPath: string | null = null;
  let screenshotUrl: string | null = null;
  if (input.screenshot) {
    try {
      screenshotPath = `reviewNotes/${input.sessionId}/${noteId}.jpg`;
      const storageRef = ref(storage, screenshotPath);
      await uploadBytes(storageRef, input.screenshot, { contentType: 'image/jpeg' });
      screenshotUrl = await getDownloadURL(storageRef);
    } catch {
      // The note is the feedback; the screenshot is illustration. Save
      // without it rather than losing what the reviewer wrote.
      screenshotPath = null;
      screenshotUrl = null;
    }
  }

  await setDoc(noteRef, {
    sessionId: input.sessionId,
    userId: input.userId,
    userAuthUid: input.userAuthUid,
    userName: input.userName,
    userEmail: input.userEmail,
    userRole: input.userRole,
    body: input.body,
    category: input.category,
    severity: input.severity,
    status: 'open',
    shapes: input.shapes,
    anchor: input.anchor,
    viewportW: input.viewportW,
    viewportH: input.viewportH,
    screenshotPath,
    screenshotUrl,
    userAgent: input.userAgent,
    appVersion: input.appVersion,
    consoleErrors: input.consoleErrors,
    assignedTo: null,
    taskRef: null,
    promotedTicketId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return noteId;
}

function toNote(id: string, d: Record<string, unknown>): ScreenReviewNote {
  return {
    id,
    sessionId: String(d.sessionId ?? ''),
    userId: String(d.userId ?? ''),
    userAuthUid: String(d.userAuthUid ?? ''),
    userName: String(d.userName ?? ''),
    userEmail: String(d.userEmail ?? ''),
    userRole: String(d.userRole ?? ''),
    body: String(d.body ?? ''),
    category: (d.category as ReviewCategory) ?? 'change',
    severity: (d.severity as ReviewSeverity) ?? 'medium',
    status: (d.status as ReviewNoteStatus) ?? 'open',
    shapes: (d.shapes as ReviewShape[]) ?? [],
    anchor: d.anchor as ReviewAnchor,
    viewportW: Number(d.viewportW ?? 0),
    viewportH: Number(d.viewportH ?? 0),
    screenshotPath: (d.screenshotPath as string) ?? null,
    screenshotUrl: (d.screenshotUrl as string) ?? null,
    userAgent: String(d.userAgent ?? ''),
    appVersion: String(d.appVersion ?? ''),
    consoleErrors: (d.consoleErrors as string[]) ?? [],
    assignedTo: (d.assignedTo as string) ?? null,
    taskRef: (d.taskRef as string) ?? null,
    promotedTicketId: (d.promotedTicketId as string) ?? null,
    aiAnalysis: (d.aiAnalysis as ScreenReviewNote['aiAnalysis']) ?? null,
    createdAt: (d.createdAt as ScreenReviewNote['createdAt']) ?? null,
    updatedAt: (d.updatedAt as ScreenReviewNote['updatedAt']) ?? null,
  };
}

/**
 * Run (or re-run with force) the server-side AI analysis of a note —
 * screenshot + description → problem summary + implementation brief.
 * Super-admin only (enforced by the Cloud Function).
 */
export async function analyzeReviewNote(
  noteId: string,
  force = false,
): Promise<{ summary: string; brief: string; cached: boolean }> {
  const callable = httpsCallable<
    { noteId: string; force: boolean },
    { summary: string; brief: string; cached: boolean }
  >(functions, 'analyzeReviewNote', { timeout: 300000 });
  const result = await callable({ noteId, force });
  return result.data;
}

/** Live subscription over all notes, newest first — the Support Hub queue. */
export function subscribeToReviewNotes(
  cb: (notes: ScreenReviewNote[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, NOTES), orderBy('createdAt', 'desc')),
    (snap) => cb(snap.docs.map((d) => toNote(d.id, d.data()))),
    (err) => {
      console.error('[screenReviewService] notes subscription failed:', err);
      onError?.(err);
    },
  );
}

export async function listSessionNotes(sessionId: string): Promise<ScreenReviewNote[]> {
  const snap = await getDocs(
    query(
      collection(db, NOTES),
      where('sessionId', '==', sessionId),
      orderBy('createdAt', 'desc'),
    ),
  );
  return snap.docs.map((d) => toNote(d.id, d.data()));
}

export async function setNoteStatus(noteId: string, status: ReviewNoteStatus): Promise<void> {
  await updateDoc(doc(db, NOTES, noteId), { status, updatedAt: serverTimestamp() });
}

export async function setNoteTriage(
  noteId: string,
  triage: { assignedTo?: string | null; taskRef?: string | null },
): Promise<void> {
  await updateDoc(doc(db, NOTES, noteId), { ...triage, updatedAt: serverTimestamp() });
}

/** Stamp the ticket a note was promoted into (the Support Hub creates the ticket). */
export async function setNotePromotedTicket(noteId: string, ticketId: string): Promise<void> {
  await updateDoc(doc(db, NOTES, noteId), {
    promotedTicketId: ticketId,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a note outright — for test notes and mistakes. The screenshot goes
 * first, best-effort: a JPEG that outlives its note is orphaned storage, but
 * an image left behind is the thing carrying data.
 */
export async function deleteNote(note: {
  id: string;
  screenshotPath: string | null;
}): Promise<void> {
  if (note.screenshotPath) {
    try {
      await deleteObject(ref(storage, note.screenshotPath));
    } catch {
      // Missing object or a storage blip — never block the actual delete.
    }
  }
  await deleteDoc(doc(db, NOTES, note.id));
}

/**
 * Count open notes for a set of nav keys via server-side aggregation — used
 * for hub stats/badges without streaming the whole collection.
 */
export async function countOpenNotes(): Promise<number> {
  try {
    const agg = await getCountFromServer(
      query(collection(db, NOTES), where('status', '==', 'open')),
    );
    return agg.data().count;
  } catch {
    return 0;
  }
}
